import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Award, Leaf, Truck, Hammer } from 'lucide-react';

const AboutUsSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: Award,
      title: "Award-Winning Design",
      description: "Recognized globally for innovative furniture designs"
    },
    {
      icon: Leaf,
      title: "Sustainable Materials",
      description: "Eco-friendly production with responsibly sourced materials"
    },
    {
      icon: Truck,
      title: "Fast Delivery",
      description: "Quick and reliable shipping to your doorstep"
    },
    {
      icon: Hammer,
      title: "Expert Craftsmanship",
      description: "Handcrafted with attention to every detail"
    }
  ];

  return (
    <div ref={sectionRef} className="py-20 px-6 bg-gradient-to-br from-teal-50 to-teal-100">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          {/* Left Side - Content */}
          <div className="space-y-8">
            <div className={`transform transition-all duration-800 ease-out ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-12 opacity-0'
            }`}>
              <p className="text-orange-500 font-semibold text-sm uppercase tracking-wide mb-2">
                About Brand Decor Furniture
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
                Welcome to Brand Decor Interiors
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                We are a furniture company that specializes in manufacturing and selling high-quality sofa sets,
                chairs, dining tables, beds and more. Beyond furniture, we also offer complete interior decor
                services to help you create warm, stylish and functional spaces that feel truly like home.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`flex items-start space-x-4 transform transition-all duration-800 ease-out ${
                    isVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
                  }`}
                  style={{ transitionDelay: `${index * 100 + 300}ms` }}
                >
                  <div className="text-teal-700 bg-white rounded-full p-3 shadow-sm">
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className={`space-y-4 transform transition-all duration-800 ease-out delay-700 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-8 opacity-0'
            }`}>
              <div className="bg-white/80 backdrop-blur-sm border border-teal-100 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-3 tracking-wide uppercase">
                  Our Location & Contacts
                </h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p>
                    <span className="font-medium">Location:</span>{' '}
                    <a
                      href="#about-location-map"
                      className="text-teal-700 hover:text-orange-500 underline-offset-2 hover:underline"
                    >
                      Near Astrol Petrol Station, Utawala, Nairobi, Kenya.
                    </a>
                  </p>
                  <p>
                    <span className="font-medium">Phone:</span> (+254) 0714 139 461
                  </p>
                  <p>
                    <span className="font-medium">WhatsApp:</span> (+254) 0714 139 461
                  </p>
                  <p>
                    <span className="font-medium">Email:</span> sales@branddecorinteriors.co.ke
                  </p>
                </div>
              </div>
              <Link
                to="/about"
                className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold hover:scale-105 transition-all duration-300"
              >
                Learn More About Us
              </Link>
            </div>
          </div>

          {/* Right Side - Image + Map */}
          <div className={`transform transition-all duration-800 ease-out delay-200 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
          }`}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Beautiful modern living room"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-500">500+</div>
                <div className="text-gray-600 text-sm">Products</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-500">10K+</div>
                <div className="text-gray-600 text-sm">Happy Customers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-500">98%</div>
                <div className="text-gray-600 text-sm">Satisfaction Rate</div>
              </div>
            </div>

            {/* Embedded Google Map */}
            <div
              id="about-location-map"
              className="mt-8 rounded-3xl overflow-hidden shadow-xl border border-teal-100 bg-white"
            >
              <div className="relative w-full h-80 sm:h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d3988.8071159810306!2d36.9499!3d-1.29!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182f1172d84d49a7%3A0xf7cf0254b297924c!2sNairobi!5e0!3m2!1sen!2ske!4v1770639472934!5m2!1sen!2ske"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="absolute inset-0 w-full h-full"
                  title="Brand Decor Location Map"
                ></iframe>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AboutUsSection;
